import { useEffect, useState, useRef } from 'preact/hooks';
import QRCode from 'qrcode';
import {
  copyToClipboard,
  formatVerseShare,
  shareNative,
  targetUrl,
  type ShareTarget,
} from '../share/share.js';
import type { ChapterVerse } from '../audio/biblebrain.js';
import type { ReaderChapter } from '../state/chapter-store.js';
import { shareCardRequest } from './ShareCardSheet.js';
import { currentTrack } from '../karaoke/highlight.js';
import { chapterStatus, playFromVerse } from '../audio/controller.js';
import { bookmarks, toggleBookmark } from '../state/bookmarks-store.js';
import {
  highlights,
  HIGHLIGHT_COLORS,
  setHighlight,
  type HighlightColor,
} from '../state/highlights-store.js';
import { getNote, setNote, removeNote } from '../state/notes-store.js';
import {
  BackArrowIcon,
  CloseIcon,
  CopyLinkIcon,
  EmailIcon,
  SmsIcon,
  TelegramIcon,
  WhatsAppIcon,
} from './player-icons.js';
import { currentBible } from '../state/bible-store.js';

type Props = {
  chapter: ReaderChapter;
  verse: ChapterVerse;
  onClose: () => void;
  onAsk: (ref: string) => void;
};

const APP_NAME = 'Scripture';

export function VerseMenu({ chapter, verse, onClose, onAsk }: Props) {
  const [view, setView] = useState<'main' | 'share-tray' | 'note' | 'qr'>('main');
  const [status, setStatus] = useState<string>('');
  const [justCopied, setJustCopied] = useState(false);
  const [noteDraft, setNoteDraft] = useState<string>(() => getNote(verse.ref)?.text ?? '');
  const chapterUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}?book=${chapter.bookId}&chapter=${chapter.chapter}`
    : '';

  const payload = formatVerseShare({
    bookName: chapter.bookName,
    chapter: chapter.chapter,
    verseNumber: verse.number,
    verseText: verse.text,
    appName: APP_NAME,
    url: chapterUrl,
  });
  const refLabel = `${chapter.bookName} ${chapter.chapter}:${verse.number}`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleShare() {
    setStatus('');
    const result = await shareNative(payload);
    if (result === 'shared' || result === 'cancelled') {
      onClose();
      return;
    }
    setView('share-tray');
  }

  async function handleCopy() {
    const copyText = `${payload.text}\nshared from ${chapterUrl}`;
    const ok = await copyToClipboard(copyText);
    setStatus(ok ? 'Copied to clipboard.' : 'Copy not available.');
    if (ok) {
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1400);
    }
  }

  function openTarget(t: ShareTarget) {
    if (t === 'copy') { void handleCopy(); return; }
    if (t === 'qr') { setView('qr'); return; }
    const url = targetUrl(t, payload);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  function hasAudioForVerse(ref: string): boolean {
    const status = chapterStatus.value;
    if (status !== 'ready' && status !== 'demo') return false;
    const cues = currentTrack.value?.verses ?? [];
    return cues.some((c) => c.verseRef === ref);
  }

  return (
    <div class="verse-menu-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div class="vas-sheet" onClick={(e) => e.stopPropagation()}>
        {view === 'main' ? (
          <>
            <div class="vas-header">
              <div class="vas-ref">{refLabel}</div>
              <button type="button" class="vas-close" onClick={onClose} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M6 6l12 12M18 6L6 18"/>
                </svg>
              </button>
            </div>

            <blockquote class="vas-quote">"{verse.text}"</blockquote>

            {/* Share | QR split bar */}
            <div class="vas-share-split">
              <button type="button" class="vas-share" onClick={handleShare} aria-label="Share">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/>
                </svg>
                Share
              </button>
              <div class="vas-divider" aria-hidden="true" />
              <button type="button" class="vas-qr" onClick={() => setView('qr')} aria-label="Share as QR code">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3.5" y="3.5" width="7" height="7" rx="1"/>
                  <rect x="6" y="6" width="2" height="2" rx=".3" style="fill:currentColor;stroke:none"/>
                  <rect x="13.5" y="3.5" width="7" height="7" rx="1"/>
                  <rect x="16" y="6" width="2" height="2" rx=".3" style="fill:currentColor;stroke:none"/>
                  <rect x="3.5" y="13.5" width="7" height="7" rx="1"/>
                  <rect x="6" y="16" width="2" height="2" rx=".3" style="fill:currentColor;stroke:none"/>
                  <path d="M13.5 13.5h2v2M17.5 13.5v2M20.5 13.5v3.5M13.5 17.5h2M17.5 17.5h3M13.5 20.5h2.5"/>
                </svg>
                QR
              </button>
            </div>

            {/* Icon tile row */}
            <div class="vas-actions">
              <button type="button" class="vas-tile" onClick={handleCopy} aria-label="Copy">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="8" y="3.5" width="12" height="14" rx="2"/>
                  <path d="M16 17.5v1.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.5"/>
                </svg>
                <span class="vas-tile-label">Copy</span>
              </button>

              <button type="button" class="vas-tile" onClick={() => { onAsk(verse.ref); onClose(); }} aria-label="Ask">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 5.5a1.8 1.8 0 0 1 1.8-1.8h8.4A1.8 1.8 0 0 1 16 5.5v4.6a1.8 1.8 0 0 1-1.8 1.8H10l-3 2.4v-2.4H5.8A1.8 1.8 0 0 1 4 10.1v-4.6z"/>
                  <path d="M8 14.5v.3a1.8 1.8 0 0 0 1.8 1.8h4.4l3 2.4v-2.4h.2A1.8 1.8 0 0 0 19 14.8v-3.3"/>
                  <path d="M8 6.5a1.8 1.8 0 0 1 3.6 0c0 1-1 1.4-1.5 1.7-.2.1-.3.4-.3.7v.2"/>
                  <circle cx="10" cy="10.5" r=".8" style="fill:currentColor;stroke:none"/>
                </svg>
                <span class="vas-tile-label">Ask</span>
              </button>

              <button
                type="button"
                class={`vas-tile${!hasAudioForVerse(verse.ref) ? ' vas-tile--dim' : ''}`}
                onClick={() => { if (hasAudioForVerse(verse.ref)) { void playFromVerse(verse.ref); onClose(); } }}
                aria-label="Play from here"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="9"/>
                  <path d="M10 8.5l5.5 3.5L10 15.5v-7z" style="fill:currentColor;stroke:none"/>
                </svg>
                <span class="vas-tile-label">Play</span>
              </button>

              <button
                type="button"
                class={`vas-tile${bookmarks.value.some((b) => b.ref === verse.ref) ? ' vas-tile--active' : ''}`}
                onClick={() => toggleBookmark({ ref: verse.ref, bookId: chapter.bookId, bookName: chapter.bookName, chapter: chapter.chapter, verseNumber: verse.number, text: verse.text })}
                aria-label="Bookmark"
                aria-pressed={bookmarks.value.some((b) => b.ref === verse.ref)}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M6 4h12v17l-6-4-6 4V4z"/>
                </svg>
                <span class="vas-tile-label">Bookmark</span>
              </button>

              <button
                type="button"
                class={`vas-tile${getNote(verse.ref) ? ' vas-tile--active' : ''}`}
                onClick={() => setView('note')}
                aria-label="Note"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 5a2 2 0 0 1 2-2h8.5L20 8.5V19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5z"/>
                  <path d="M14 3v5h5"/>
                  <line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="16.5" x2="13" y2="16.5"/>
                </svg>
                <span class="vas-tile-label">Note</span>
              </button>

              <button
                type="button"
                class="vas-tile"
                onClick={() => {
                  shareCardRequest.value = { kind: 'verse', verseText: verse.text, reference: refLabel };
                  onClose();
                }}
                aria-label="Share image"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="18" height="14" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span class="vas-tile-label">Image</span>
              </button>
            </div>

            <HighlightRow verseRef={verse.ref} />
            {status ? <p class="verse-menu-status" aria-live="polite">{status}</p> : null}
          </>
        ) : view === 'note' ? (
          <NoteView
            verseRef={verse.ref}
            refLabel={refLabel}
            draft={noteDraft}
            setDraft={setNoteDraft}
            onBack={() => setView('main')}
            onClose={onClose}
          />
        ) : view === 'qr' ? (
          <QrCodeView
            url={chapterUrl}
            refLabel={refLabel}
            verseText={verse.text}
            onBack={() => setView('main')}
            onClose={onClose}
          />
        ) : (
          <ShareTray
            refLabel={refLabel}
            verseNumber={verse.number}
            verseText={verse.text}
            onPick={openTarget}
            onBack={() => setView('main')}
            onClose={onClose}
            justCopied={justCopied}
          />
        )}
      </div>
    </div>
  );
}

type ShareTrayProps = {
  refLabel: string;
  verseNumber: number;
  verseText: string;
  onPick: (t: ShareTarget) => void;
  onBack: () => void;
  onClose: () => void;
  justCopied: boolean;
};

function ShareTray({
  refLabel,
  verseNumber,
  verseText,
  onPick,
  onBack,
  onClose,
  justCopied,
}: ShareTrayProps) {
  const targets: { target: ShareTarget; label: string; icon: typeof WhatsAppIcon }[] = [
    { target: 'whatsapp', label: 'WhatsApp', icon: WhatsAppIcon },
    { target: 'telegram', label: 'Telegram', icon: TelegramIcon },
    { target: 'email', label: 'Email', icon: EmailIcon },
    { target: 'sms', label: 'Messages', icon: SmsIcon },
    { target: 'copy', label: 'Copy link', icon: CopyLinkIcon },
  ];

  return (
    <div class="share-tray">
      <header class="share-tray-header">
        <button
          type="button"
          class="share-tray-chip"
          onClick={onBack}
          aria-label="Back"
        >
          <BackArrowIcon size={18} />
        </button>
        <span class="share-tray-title">Share verse</span>
        <button
          type="button"
          class="share-tray-chip"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon size={18} />
        </button>
      </header>
      <div class="share-tray-preview">
        <span class="share-tray-preview-num">{verseNumber}</span>
        <p class="share-tray-preview-text">{verseText}</p>
        <span class="share-tray-preview-ref">{refLabel}</span>
      </div>
      <div class="share-tray-grid">
        {targets.map(({ target, label, icon: Icon }) => {
          const active = target === 'copy' && justCopied;
          return (
            <button
              key={target}
              type="button"
              class={`share-tray-target${active ? ' is-copied' : ''}`}
              onClick={() => onPick(target)}
            >
              <span class="share-tray-target-tile">
                <Icon size={22} />
              </span>
              <span class="share-tray-target-label">
                {target === 'copy' && justCopied ? 'Copied' : label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

type QrCodeViewProps = {
  url: string;
  refLabel: string;
  verseText: string;
  onBack: () => void;
  onClose: () => void;
};

function QrCodeView({ url, refLabel, verseText, onBack, onClose }: QrCodeViewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [walletStatus, setWalletStatus] = useState<string>('');

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || !url) return;
    void QRCode.toCanvas(el, url, {
      width: 220,
      margin: 2,
      color: { dark: '#1a1a1a', light: '#ffffff' },
    });
  }, [url]);

  async function addToAppleWallet() {
    setWalletStatus('Preparing pass…');
    const { displayName, bibleId, languageIso } = currentBible.value;
    const version = bibleId.slice(3).replace(/[0-9].*$/, '').slice(0, 6);
    const params = new URLSearchParams({
      ref: refLabel,
      text: verseText,
      url,
      bible: displayName,
      bibleId,
      lang: languageIso,
      version,
    });
    try {
      const res = await fetch(`/api/wallet/apple?${params}`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({})) as { error?: string };
        setWalletStatus(j.error ?? 'Not available right now');
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${refLabel.replace(/[\s:]/g, '-')}.pkpass`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      setWalletStatus('');
    } catch {
      setWalletStatus('Not available right now');
    }
  }

  async function addToGoogleWallet() {
    setWalletStatus('Opening Google Wallet…');
    const params = new URLSearchParams({ ref: refLabel, text: verseText, url });
    try {
      const res = await fetch(`/api/wallet/google?${params}`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({})) as { error?: string };
        setWalletStatus(j.error ?? 'Not available right now');
        return;
      }
      const { saveUrl } = await res.json() as { saveUrl: string };
      window.open(saveUrl, '_blank', 'noopener,noreferrer');
      setWalletStatus('');
    } catch {
      setWalletStatus('Not available right now');
    }
  }

  return (
    <div class="share-qr">
      <header class="share-tray-header">
        <button type="button" class="share-tray-chip" onClick={onBack} aria-label="Back">
          <BackArrowIcon size={18} />
        </button>
        <span class="share-tray-title">Scan to open</span>
        <button type="button" class="share-tray-chip" onClick={onClose} aria-label="Close">
          <CloseIcon size={18} />
        </button>
      </header>
      <div class="share-qr-body">
        <div class="share-qr-frame">
          <canvas ref={canvasRef} class="share-qr-canvas" aria-label="QR code" />
        </div>
        <p class="share-qr-label">{refLabel}</p>
        <p class="share-qr-hint">Point a camera at this code to open this chapter</p>
        <div class="share-qr-wallets">
          <button
            type="button"
            class="share-qr-wallet-btn share-qr-wallet-btn--apple"
            onClick={addToAppleWallet}
            aria-label="Add to Apple Wallet"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Add to Apple Wallet
          </button>
          <button
            type="button"
            class="share-qr-wallet-btn share-qr-wallet-btn--google"
            onClick={addToGoogleWallet}
            aria-label="Save to Google Wallet"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Save to Google Wallet
          </button>
        </div>
        {walletStatus ? <p class="share-qr-wallet-status" aria-live="polite">{walletStatus}</p> : null}
      </div>
    </div>
  );
}

const SWATCH_COLORS: Record<HighlightColor, string> = {
  gold: '#f1c54a',
  rose: '#e98aa1',
  sage: '#7fc28d',
  sky:  '#7aa3df',
};

function HighlightRow({ verseRef }: { verseRef: string }) {
  const current = highlights.value[verseRef] ?? null;
  return (
    <div class="vas-highlights" role="radiogroup" aria-label="Highlight color">
      <div class="vas-swatches">
        {HIGHLIGHT_COLORS.map((color) => {
          const active = color === current;
          return (
            <button
              key={color}
              type="button"
              class="vas-swatch"
              aria-label={`Highlight ${color}`}
              aria-pressed={active}
              style={{ background: SWATCH_COLORS[color as HighlightColor] }}
              onClick={() => setHighlight(verseRef, active ? null : (color as HighlightColor))}
            />
          );
        })}
      </div>
      <button
        type="button"
        class="vas-clear"
        disabled={!current}
        onClick={() => setHighlight(verseRef, null)}
      >
        Clear
      </button>
    </div>
  );
}

type NoteViewProps = {
  verseRef: string;
  refLabel: string;
  draft: string;
  setDraft: (v: string) => void;
  onBack: () => void;
  onClose: () => void;
};

function NoteView({ verseRef, refLabel, draft, setDraft, onBack, onClose }: NoteViewProps) {
  const save = (): void => {
    setNote(verseRef, draft);
    onClose();
  };
  const del = (): void => {
    removeNote(verseRef);
    onClose();
  };
  return (
    <div class="verse-menu-note">
      <header class="verse-menu-note-header">
        <button type="button" class="verse-menu-back" onClick={onBack} aria-label="Back">←</button>
        <span class="verse-menu-note-ref">{refLabel}</span>
        <button type="button" class="verse-menu-close" onClick={onClose} aria-label="Close">×</button>
      </header>
      <textarea
        class="verse-menu-note-text"
        placeholder="Write a note about this verse…"
        value={draft}
        onInput={(e) => setDraft((e.currentTarget as HTMLTextAreaElement).value)}
        autoFocus
      />
      <div class="verse-menu-note-actions">
        {getNote(verseRef) ? (
          <button type="button" class="verse-menu-btn" onClick={del}>
            Delete
          </button>
        ) : null}
        <button type="button" class="verse-menu-btn primary" onClick={save}>
          Save
        </button>
      </div>
    </div>
  );
}

